import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Divider from 'apollo-react/components/Divider';

import Typography from '../../StyledTypography';
import CommentViewer from '../CommentViewer';
import CommentToggle from '../CommentToggle';
import useSubformCommentsApi from '../../../../hooks/api/comments/useSubformCommentsApi';
import styles from './SubformCommentSection.module.css';

const SubformCommentSection = ({
  requestId,
  subFormId,
  formId,
  requestProgress,
  requestDateEnded,
  currentUserEmail,
  enableAddComment = false,
  onSubformCommentAdded,
}) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addComment, getCommentsBySubform } = useSubformCommentsApi();

  const fetchComments = useCallback(async () => {
    if (!requestId || !subFormId) return;
    try {
      setLoading(true);
      const data = await getCommentsBySubform(requestId, subFormId, formId);
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching subform comments:', error);
    } finally {
      setLoading(false);
    }
  }, [requestId, subFormId, formId, getCommentsBySubform]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleAddComment = useCallback(
    async ({ comment, attachedFiles }) => {
      if (!requestId) return null;
      try {
        const newComment = await addComment({
          comment,
          requestId,
          subFormId,
          formId,
          attachedFiles,
        });
        setComments((prev) => [newComment, ...prev]);
        onSubformCommentAdded?.();
      } catch (error) {
        console.error('Error adding subform comment:', error);
      }
    },
    [addComment, requestId, subFormId, formId, onSubformCommentAdded]
  );

  if (loading) return null;

  if (!requestId) return null;

  if (comments.length === 0 && !enableAddComment) return null;

  return (
    <div className={styles.container}>
      {comments.length > 0 && (
        <>
          <Typography
            variant="title1"
            component="h2"
            className={styles.commentLabel}
          >
            {t('comments')}
          </Typography>
          <Divider className={styles.sectionDivider} />
        </>
      )}

      {comments.length > 0 && (
        <CommentViewer
          comments={comments}
          compact={false}
          hideHeader={true}
          requestProgress={requestProgress}
          requestDateEnded={requestDateEnded}
          isSubformComment={true}
          currentUserEmail={currentUserEmail}
          requestId={requestId}
          onCommentUpdated={(updatedComment) => {
            setComments((prev) =>
              prev.map((c) =>
                c.comment?.id === updatedComment.id
                  ? { ...c, comment: updatedComment }
                  : c
              )
            );
          }}
          onCommentDeleted={(commentId) => {
            setComments((prev) =>
              prev.filter((c) => c.comment?.id !== commentId)
            );
          }}
        />
      )}

      {enableAddComment && (
        <CommentToggle
          onAddComment={handleAddComment}
          buttonText={t('add_comment')}
          hasComments={comments.length > 0}
        />
      )}
    </div>
  );
};

export default SubformCommentSection;
